// Total unread chat messages for the logged-in user, kept live off the chat socket.
// Used by the sidebar to badge the "Chat" item. Best-effort: any failure just
// leaves the badge at its last value — it must never break navigation.

import { useCallback, useEffect, useState } from "react";
import { listConversations } from "../../redux/apis/chatAPIs";
import { getChatSocket } from "../../config/chatSocket";

export const useChatUnread = (role: string | null): number => {
  const [unread, setUnread] = useState(0);
  // The admin (distributor) is a read-only monitor and has no unread state.
  const enabled = role === "retailer" || role === "sales";

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const lists =
        role === "sales"
          ? await Promise.all([
              listConversations("retailer_sales"),
              listConversations("sales_sales"),
            ])
          : [await listConversations("retailer_sales")];
      const total = lists
        .flat()
        .reduce((sum, c: any) => sum + (Number(c?.unreadCount) || 0), 0);
      setUnread(total);
    } catch {
      /* keep the previous value */
    }
  }, [enabled, role]);

  useEffect(() => {
    if (!enabled) {
      setUnread(0);
      return;
    }
    refresh();

    const socket = getChatSocket();
    const onChange = () => refresh();
    // A new message can raise the count; a read receipt lowers it. Promo broadcasts
    // count too — they arrive on their own event and ride along in the retailer list.
    socket.on("chat:message", onChange);
    socket.on("chat:broadcast", onChange);
    socket.on("chat:read", onChange);
    return () => {
      socket.off("chat:message", onChange);
      socket.off("chat:broadcast", onChange);
      socket.off("chat:read", onChange);
    };
  }, [enabled, refresh]);

  return unread;
};

export default useChatUnread;
